import getLocalStorageKey from 'api/browser/localstorage/get';
import removeLocalStorageKey from 'api/browser/localstorage/remove';
import setLocalStorageKey from 'api/browser/localstorage/set';
import { LOCALSTORAGE } from 'constants/localStorage';
import { isObject } from 'lodash-es';

/**
 * 시간 범위는 페이지마다 따로가 아니라 앱 전체에서 하나를 공유한다.
 * 서비스 상세에서 1주로 맞춰 두고 로그/트레이스로 넘어가도 그대로 유지된다.
 *
 * 저장 값 형식은 두 가지다.
 *  - 상대 범위: 프리셋 문자열 그대로 (예: '1w')
 *  - 절대 구간: `{ startTime, endTime }`를 JSON.stringify 한 문자열
 * 파싱은 읽는 쪽(DateTimeSelectionV2)이 기존 방식 그대로 담당한다.
 */

/** 마지막으로 선택한 시간 범위를 저장한다. */
export function persistGlobalTimeDuration(value: string): void {
	setLocalStorageKey(LOCALSTORAGE.GLOBAL_TIME_DURATION, value);
}

/**
 * 저장된 시간 범위를 원본 문자열로 돌려준다. 저장된 적이 없으면 null.
 *
 * 예전에는 `metricsTimeDurations`에 경로별 맵으로 저장했다. 어느 경로 값을
 * 전역 값으로 승계할지 근거가 없어 승계하지 않고, 눈에 띌 때 지우기만 한다.
 */
export function readGlobalTimeDuration(): string | null {
	const value = getLocalStorageKey(LOCALSTORAGE.GLOBAL_TIME_DURATION);

	if (!value) {
		removeLocalStorageKey(LOCALSTORAGE.METRICS_TIME_IN_DURATION);
		return null;
	}

	return value;
}

/** 저장된 절대 구간. dayjs가 JSON으로 직렬화한 ISO 문자열이다. */
export interface GlobalTimeAbsoluteRange {
	startTime: string;
	endTime: string;
}

export interface GlobalTimeRange {
	/** 상대 범위 프리셋. 절대 구간이 저장돼 있으면 null. */
	preset: string | null;
	/** 절대 구간. 상대 범위가 저장돼 있으면 null. */
	absolute: GlobalTimeAbsoluteRange | null;
}

const EMPTY_RANGE: GlobalTimeRange = { preset: null, absolute: null };

/** 저장된 시간 범위를 상대/절대로 구분해 돌려준다. */
export function readGlobalTimeRange(): GlobalTimeRange {
	const value = readGlobalTimeDuration();

	if (!value) {
		return EMPTY_RANGE;
	}

	let parsed: unknown;
	try {
		parsed = JSON.parse(value);
	} catch {
		// '1w' 같은 프리셋은 JSON이 아니다.
		return { preset: value, absolute: null };
	}

	if (isObject(parsed)) {
		return { preset: null, absolute: parsed as GlobalTimeAbsoluteRange };
	}

	// '1month'처럼 JSON으로는 파싱되지만 객체가 아닌 값도 프리셋으로 본다.
	return { preset: value, absolute: null };
}
